import { isClerkAPIResponseError } from "@clerk/nextjs/errors";
import { Component, type ErrorInfo, type ReactNode } from "react";

import { logger } from "@/util/logger";

interface Props {
  children: ReactNode;
  onError?: (error: Error, errorInfo: ErrorInfo) => void;
}

interface State {
  hasError: boolean;
  error?: Error;
}

/**
 * Error boundary specifically for Clerk-related errors.
 * Catches and handles authentication errors that might not be caught elsewhere.
 */
export class ClerkErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error): State {
    // Update state so the next render will show the fallback UI
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    // Log the error
    logger.error("Clerk Error Boundary caught an error:", error, errorInfo);

    // Check if it's a Clerk error and handle accordingly
    if (isClerkAPIResponseError(error)) {
      logger.error("Clerk API Error:", {
        message: error.message,
        errors: error.errors,
        clerkTraceId: error.clerkTraceId,
      });
    }

    // Call the optional error handler
    this.props.onError?.(error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      // For Clerk errors, we typically don't want to show a broken UI
      // Instead, we'll let the error be handled by the error handlers
      // and just continue rendering the children
      return this.props.children;
    }

    return this.props.children;
  }
}
